import { alpha, createTheme } from '@mui/material/styles';

const SCALE_STEPS = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

// Neutral scales per mode (Tailwind "zinc"-like). In dark mode the scale is inverted,
// so 0 is always the card surface and 900 is always the strongest foreground.
const SCALES = {
  light: {
    0: '#FFFFFF',
    50: '#FAFAFA',
    100: '#F4F4F5',
    200: '#E4E4E7',
    300: '#D4D4D8',
    400: '#A1A1AA',
    500: '#7A7A83',
    600: '#63636B',
    700: '#5A5A62',
    800: '#48484F',
    900: '#35353B',
  },
  dark: {
    0: '#18181B',
    50: '#1E1E22',
    100: '#26262B',
    200: '#303036',
    300: '#3F3F46',
    400: '#63636B',
    500: '#9A9AA3',
    600: '#B8B8C0',
    700: '#CFCFD4',
    800: '#DCDCE0',
    900: '#EDEDF0',
  },
};

const CANVAS_BY_MODE = { light: '#F5F5F6', dark: '#0E0E10' };

const SHADOWS_BY_MODE = {
  light: {
    card: '0 1px 2px rgba(16, 24, 40, 0.04), 0 1px 3px rgba(16, 24, 40, 0.06)',
    cardHover: '0 2px 4px rgba(16, 24, 40, 0.05), 0 6px 16px rgba(16, 24, 40, 0.08)',
    overlay: '0 16px 48px rgba(0, 0, 0, 0.08)',
  },
  dark: {
    card: '0 1px 2px rgba(0, 0, 0, 0.4)',
    cardHover: '0 2px 4px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.45)',
    overlay: '0 16px 48px rgba(0, 0, 0, 0.6)',
  },
};

// Components reference these CSS variables, so they follow the active mode without
// needing the theme object. Don't feed them to alpha()/darken(); use STATUS for that.
export const NEUTRAL = Object.fromEntries(SCALE_STEPS.map((step) => [step, `var(--pm-n${step})`]));
export const CANVAS = 'var(--pm-canvas)';
export const CARD_SHADOW = 'var(--pm-shadow-card)';
export const CARD_SHADOW_HOVER = 'var(--pm-shadow-card-hover)';

// Light status colors for dots, rings, bars and tinted fills.
export const STATUS = {
  healthy: '#86D9A5',
  warning: '#F4CD7A',
  critical: '#F2A0A0',
  info: '#9CC3F5',
  unknown: '#C4C4CC',
};

// Readable status colors for text and icons on cards (set per mode via CSS variables).
export const STATUS_TEXT = {
  healthy: 'var(--pm-healthy-text)',
  warning: 'var(--pm-warning-text)',
  critical: 'var(--pm-critical-text)',
  info: 'var(--pm-info-text)',
  unknown: 'var(--pm-n500)',
};

const STATUS_TEXT_BY_MODE = {
  light: { healthy: '#3E9A63', warning: '#B7862B', critical: '#CF6464', info: '#5288CC' },
  dark: { healthy: '#86D9A5', warning: '#F4CD7A', critical: '#F2A0A0', info: '#9CC3F5' },
};

const TEXT_BY_STATUS_COLOR = Object.fromEntries(
  Object.entries(STATUS).map(([key, color]) => [color, STATUS_TEXT[key]])
);

// Map a STATUS color to its readable text counterpart (other values pass through).
export const textFor = (color) => TEXT_BY_STATUS_COLOR[color] || color;

// Soft tinted backgrounds for chips/badges.
export const statusTint = (color, amount = 0.14) => alpha(color, amount);

export const SEVERITY_COLORS = {
  critical: { bg: statusTint(STATUS.critical), color: STATUS_TEXT.critical },
  warning: { bg: statusTint(STATUS.warning), color: STATUS_TEXT.warning },
  info: { bg: statusTint(STATUS.info), color: STATUS_TEXT.info },
};

const typography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: 14,
  h1: { fontWeight: 600, fontSize: '2rem', letterSpacing: '-0.02em' },
  h2: { fontWeight: 600, fontSize: '1.625rem', letterSpacing: '-0.015em' },
  h3: { fontWeight: 600, fontSize: '1.375rem', letterSpacing: '-0.01em' },
  h4: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.01em' },
  h5: { fontWeight: 600, fontSize: '1.125rem' },
  h6: { fontWeight: 600, fontSize: '1rem' },
  subtitle1: { fontWeight: 500, fontSize: '0.9375rem' },
  subtitle2: { fontWeight: 500, fontSize: '0.8125rem' },
  body1: { fontSize: '0.875rem' },
  body2: { fontSize: '0.8125rem' },
  button: { fontWeight: 500, textTransform: 'none', letterSpacing: 0 },
  caption: { fontSize: '0.75rem' },
  overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
};

export const createAppTheme = (mode = 'light') => {
  const n = SCALES[mode] || SCALES.light;
  const canvas = CANVAS_BY_MODE[mode] || CANVAS_BY_MODE.light;
  const shadows = SHADOWS_BY_MODE[mode] || SHADOWS_BY_MODE.light;

  const cssVars = {
    ...Object.fromEntries(SCALE_STEPS.map((step) => [`--pm-n${step}`, n[step]])),
    ...Object.fromEntries(
      Object.entries(STATUS_TEXT_BY_MODE[mode] || STATUS_TEXT_BY_MODE.light).map(([key, value]) => [`--pm-${key}-text`, value])
    ),
    '--pm-canvas': canvas,
    '--pm-shadow-card': shadows.card,
    '--pm-shadow-card-hover': shadows.cardHover,
  };

  return createTheme({
    palette: {
      mode,
      primary: { main: n[900], light: n[700], dark: n[800], contrastText: n[0] },
      secondary: { main: n[600], contrastText: n[0] },
      success: { main: (STATUS_TEXT_BY_MODE[mode] || STATUS_TEXT_BY_MODE.light).healthy },
      warning: { main: (STATUS_TEXT_BY_MODE[mode] || STATUS_TEXT_BY_MODE.light).warning },
      error: { main: (STATUS_TEXT_BY_MODE[mode] || STATUS_TEXT_BY_MODE.light).critical },
      info: { main: (STATUS_TEXT_BY_MODE[mode] || STATUS_TEXT_BY_MODE.light).info },
      background: { default: canvas, paper: n[0] },
      text: { primary: n[900], secondary: n[500], disabled: n[400] },
      divider: n[200],
      grey: n,
      action: {
        hover: n[50],
        selected: n[100],
        focus: n[100],
        disabled: n[400],
        disabledBackground: n[100],
      },
    },
    shape: { borderRadius: 6 },
    typography,
    shadows: ['none', ...Array(24).fill(shadows.card)],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': { ...cssVars, colorScheme: mode },
          body: { backgroundColor: canvas },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: n[200] },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: { borderColor: n[200], borderRadius: 10, boxShadow: shadows.card, backgroundColor: n[0] },
        },
      },
      MuiTableContainer: {
        styleOverrides: { root: { backgroundColor: n[0] } },
      },
      MuiToggleButtonGroup: {
        styleOverrides: { root: { backgroundColor: n[0] } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderColor: n[300],
            color: n[500],
            padding: '6px 9px',
            '&.Mui-selected': { backgroundColor: n[100], color: n[900] },
            '&.Mui-selected:hover': { backgroundColor: n[200] },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { border: `1px solid ${n[200]}`, boxShadow: shadows.overlay },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { border: `1px solid ${n[200]}`, boxShadow: shadows.overlay },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(24, 24, 27, 0.32)' },
          invisible: { backgroundColor: 'transparent' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500 },
          outlined: {
            borderColor: n[300],
            color: n[800],
            backgroundColor: n[0],
            '&:hover': { borderColor: n[400], backgroundColor: n[50] },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 6 } },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 4, fontWeight: 500 },
          outlined: { borderColor: n[200] },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { height: 2, backgroundColor: n[900] },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            fontWeight: 500,
            textTransform: 'none',
            color: n[500],
            '&.Mui-selected': { color: n[900] },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: n[200] },
          head: { color: n[500], fontWeight: 500, backgroundColor: n[50] },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: n[900], color: n[0], fontSize: '0.75rem', fontWeight: 400 },
          arrow: { color: n[900] },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { backgroundColor: n[0] },
          notchedOutline: { borderColor: n[300] },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': { color: n[0] },
            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: n[900], opacity: 1 },
          },
        },
      },
    },
  });
};
